import $ from 'jquery';

import { getLocale } from '@lib/i18n';

import { IUTMParams } from '@lib/utils';
import { getStaticHostUrl } from '@lib/environments.const';

const STATIC_HOST_URL = getStaticHostUrl();

export const UPSELL_FREEMIUM_ID = {
  fuel_insights: 'freemium-fuel-insights',
  fuel_insights_score: 'freemium-fuel-insights-score',
  ifta: 'freemium-ifta',
  maintenance: 'freemium-maintenance',
  safety: 'freemium-safety',
  vehicle_utilization: 'freemium-vehicle-utilization',
  all_plans_vg_upgrade: 'all-plans-vg-upgrade',
};

export const UPSELL_MODE = {
  upgrade: 'Upgrade',
  trial: 'Trial',
};

export function isValidUpsellFeatureKey(key: string): boolean {
  return UPSELL_CONFIG.hasOwnProperty(key);
}

export function upsellEventId(elem: HTMLElement, key: string): string {
  const isTrialBtn: any = $(elem).find('.try-for-free-btn');
  const isReqReceivedBtn: any = $(elem).find('.request-received-btn');

  const isTrialBtnVisible: boolean = isTrialBtn.length > 0 && isTrialBtn.offsetParent !== null;
  const isReqReceivedBtnVisible: boolean = isReqReceivedBtn.length > 0 && isReqReceivedBtn.offsetParent !== null;

  const upsellId: string = UPSELL_CONFIG[key].upsellId;
  const trialUpsellID: string = `${(UPSELL_MODE.trial).toLowerCase()}-${upsellId}`;
  const upgradeUpsellID: string = `${(UPSELL_MODE.upgrade).toLowerCase()}-${upsellId}`;
  const upgradeReceivedUpsellID: string = `${upgradeUpsellID}-requested`;

  let id: string = '';
  if (isTrialBtnVisible) {
    id = trialUpsellID;
  } else if (isReqReceivedBtnVisible) {
    id = upgradeReceivedUpsellID;
  } else {
    id = upgradeUpsellID;
  }

  return id || '';
}

// added dynamically via getFlagVariation in upsell-entry-popup
export const FEATURE_FLAG_UPSELL_CONFIG_KEYS = {
  CARDS_UPSELL_POPUP_ADMIN_V1: 'cards_upsell_popup_admin_v1',
  CARDS_UPSELL_POPUP_ADMIN_V2: 'cards_upsell_popup_admin_v2',
  CARDS_UPSELL_POPUP_NON_ADMIN: 'cards_upsell_popup_non_admin',
  EOY_2022_GENERAL_DASHCAM_UPSELL: 'eoy_2022_general_dashcam_upsell',
  EOY_2022_FLEETVIEW_DASHCAM_UPSELL: 'eoy_2022_fleetview_dashcam_upsell',
  EOQ_Q1_2023_UPSELL: 'fg-eoq-upsell-q1-2023',
};


export const CAMPAIGN_MAP = {
  cmrl_mm_ent_expansion: 'get_demo_ai_dashcam',
  smb_promo_expansion: 'smb_promo',
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_ADMIN_V1]: 'cards_upsell_pop_up',
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_ADMIN_V2]: 'cards_upsell_pop_up',
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_NON_ADMIN]: 'cards_upsell_pop_up',
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOY_2022_GENERAL_DASHCAM_UPSELL]: 'general_dashcam_upsell',
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOY_2022_FLEETVIEW_DASHCAM_UPSELL]: 'fleetview_dashcam_upsell',
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOQ_Q1_2023_UPSELL]: 'smb_cards_sweepstakes',
};

export interface IUpgradeOptions {
  upsellId?: string,
  imgUrl?: string;
  videoUrl?: string;
  staticImgUrl?: string;
  title: string;
  description?: string;
  description_with_updatedDate?: string
  description_with_updatedBy_and_updatedDate?: string;
  fiveCentsOffDescription?: string;
  threeCentsOffDescription?: string;
  checkList?: string[];
  learnMoreUrl?: string;
  isTrialBtn?: boolean;
  isUpgradeBtn?: boolean;
  isGetQuoteBtn?: boolean;
  isGetDemoBtn?: boolean;
  isActivateSaving?: boolean;
  isGetTheDealBtn?: boolean;
  isUpgradeRequestedBtn?: boolean;
  isGetQuoteRequestedBtn?: boolean;
  isGetDemoRequestedBtn?: boolean;
  isActivateSavingRequestedBtn?: boolean;
  isGetTheDealRequestedBtn?: boolean;
  isApplyNowBtn?: boolean;
  isImInterestedBtn?: boolean;
  successDescription?: string;
  phoneNumberText?: string;
  phoneNumberTel?: string;
  getDemoBtnText?: string;
  wrapDescText?: boolean;
  learnMoreFcUrl?: string;
  isCompleteMyApplicationBtn?: boolean
}

export interface IAddUnit {
  title?: string;
  imgUrl?: string;
  pardotField?: string;
  type: string;
  quantity?: number;
}

export interface IAddUnits {
  units: IAddUnit[];
  isTrialBtn?: boolean;
  isUpgradeBtn?: boolean;
  isGetQuoteBtn?: boolean;
  isUpgradeRequestedBtn?: boolean;
  isGetQuoteRequestedBtn?: boolean;
}

export interface ISuccess {
  heading?: string;
  description?: string;
  featuresList?: string[];
}

export interface IGenericUpsellDetails {
  campaign_type?: string;
  cta_content: string;
  product_interest: string;
  upsell_product_interest: string;
  fe_test_info: string;
  utm_params: IUTMParams;
  offer_type: string;

  // This property originated from the website
  // using it here as the current page's url in webapp
  url_of_most_recent_mst_fill: string;
}

interface IVideoLocales {
  'en-US': string;
  'fr-CA': string;
}

export interface IInsuranceUpsellDynamicConfig {
  guideId: string;
  guideName: string;
  featureFlagName: string;
}

export interface IInsuranceUpsellDynamicConfigs {
  guidesData: IInsuranceUpsellDynamicConfig[];
}

export interface IGeneralUpsellParams {
  subscribeTo: string;
  userEmail: string;
  firstName: string;
  lastName: string;
  companyName: string;
  productInterest?: string;
  comments?: string;
  upsellKey?: string;
  ctaContent?: string; // this refers to the hard coded cta content in k2web
  feTestInfo?: string;
}

export interface ISalesForceOpportunityParams extends IUTMParams {
  user_kt_id: number;
}

export function getStaticImgUrl(directory: string, src: string): string {
  return `${STATIC_HOST_URL}/${directory}/${src}`;
}

export const ADD_DEVICES_UPSELL_CONFIG: { [key: string]: IAddUnits } = {
  addDevices: {
    units: [
      {
        title: $localize`Vehicle Gateway`,
        imgUrl: getStaticImgUrl('upsell', 'vehicle-gateway.png'),
        type: 'vehicle_gateway',
        pardotField: null,
        quantity: 0,
      },
      {
        title: $localize`Asset Gateway`,
        imgUrl: getStaticImgUrl('upsell', 'asset-gateway.png'),
        type: 'asset_gateway',
        pardotField: null,
        quantity: 0,
      },
      {
        title: $localize`Dual-Facing Smart Dashcam`,
        imgUrl: getStaticImgUrl('upsell', 'dual-facing-smart-dashcam.png'),
        type: 'dual_facing_smart_dashcam',
        pardotField: null,
        quantity: 0,
      },
      {
        title: $localize`Road-Facing Smart Dashcam`,
        imgUrl: getStaticImgUrl('upsell', 'road-facing-smart-dashcam.png'),
        type: 'road_facing_smart_dashcam',
        pardotField: null,
        quantity: 0,
      },
    ],
    isGetQuoteBtn: true,
    isGetQuoteRequestedBtn: false,
  },
};

const UPSELL_VIDEOS: { [key: string]: IVideoLocales } = {
  safety: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/Safety_3.0___Dashboard_Upsell.mp4',
    'fr-CA': '',
  },
  ifta: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/video-fuel-hub-english.mp4',
    'fr-CA': 'https://go.gomotive.com/rs/264-HRT-423/images/video-fuel-hub-french.mp4',
  },
  addDashCam: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/kt_smart_dashcam.mp4',
    'fr-CA': '',
  },
  addDashCamEvents: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/kt_smart_dashcam.mp4',
    'fr-CA': '',
  },
  addDashCamFleetLive: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/kt_smart_dashcam.mp4',
    'fr-CA': '',
  },
  vehicleUtilization: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/video-fuel-hub-english.mp4',
    'fr-CA': 'https://go.gomotive.com/rs/264-HRT-423/images/video-fuel-hub-french.mp4',
  },
  agUpsell: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/upsell_ag_video.mp4',
    'fr-CA': 'https://go.gomotive.com/rs/264-HRT-423/images/video-fleet-view-french.mp4',
  },
  vgUpsell: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/video-upsell-vg.mp4',
    'fr-CA': 'https://go.gomotive.com/rs/264-HRT-423/images/video-tracking-2-french.mp4',
  },
  general_upgrade: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/kt_general_upgrade_pro.mp4',
    'fr-CA': '',
  },
  fuel: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/video-fuel-hub-english.mp4',
    'fr-CA': 'https://go.gomotive.com/rs/264-HRT-423/images/video-fuel-hub-french.mp4',
  },
  maintenance: {
    'en-US': 'https://go.gomotive.com/rs/264-HRT-423/images/Maintenance%20Upsell%20%28Dashboard%29.mp4',
    'fr-CA': '',
  },
};

function getLocaleSpecificVideoLink(feature: string): string {
  const videoLink = UPSELL_VIDEOS[feature][getLocale()];
  return videoLink ? videoLink : UPSELL_VIDEOS[feature]['en-US'];
}

function updateUTMCampaign(utmCampaign: string) {
  return { ...DEFAULT_UPSELL_PARAMS.utm_params, utm_campaign: utmCampaign };
}

export enum UPSELL_CAMPAIGN_NAMES {
  upgrade_now = 'WAPP-2021-10-Upgrade-Now',

  get_quote = 'WAPP-2021-10-Get-Quote',
  get_quote_vg = 'WAPP-2021-10-Get-Quote-for-Vehicle-Gateway',
  get_quote_ag = 'WAPP-2021-10-Get-Quote-for-Asset-Gateway',
  get_quote_dashcam = 'WAPP-2021-10-Get-Quote-for-Smart-Dashcam',
  get_quote_add_more_units = 'WAPP-2021-10-Get-Quote-for-Adding-More-Units',

  cpq_add = 'WAPP-2022-01-Freemium-Hardware-Upsell',
  cpq_upgrade = 'WAPP-2022-01-Freemium-Plan-Upsell',

  shop_evergreen_upsell = 'WAPP-2024-5-Evergreen-CTA-Non-Ecomm-Customers',

  maintenance_pro = 'WAPP-2022-01-Maintenance-Pro-Upsell',

  get_demo_ai_dashcam = 'WAPP-2022-05-AI-Dashcam-Upsell',
  general_dashcam_upsell = 'WAPP-General-Dashcam-Upsell',
  fleetview_dashcam_upsell = 'WAPP-Fleetview-Dashcam-Upsell',

  smb_promo = 'WAPP-2022-05-SMB-Promo-Upsell',
  smb_cards_sweepstakes = 'WAPP-2023-03-SMB-Cards-Sweepstakes',

  fleet_cards = 'WAPP-2022-01-Cards-Upsell',
  fleet_cards_estimated_savings_apply_now = 'WAPP_2025-02_Savings-Estimates-Apply-Now',
  cards_upsell_pop_up = 'WAPP-Pop-Up-Cards-Upsell',
  savings_calculator = 'WAPP_Cards_Savings_Calculator',

  // The cards_upsell_pop_up will be used for the following
  // only CTA Content will be different
  cards_upsell_pop_up_apply_now = 'WAPP-Pop-Up-Cards-Upsell-Apply-Now',
  cards_upsell_pop_up_interested = 'WAPP-Pop-Up-Cards-Upsell-Interested',
}

export const UPSELL_CONFIG: { [key: string]: IUpgradeOptions } = {
  safety: {
    upsellId: 'safety',
    staticImgUrl: getStaticImgUrl('upsell', 'safety-3-0-sizzle-reel-thumbnail.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('safety'),
    title: $localize`Upgrade to Pro to unlock the Safety Hub`,
    description: $localize`Prevent accidents and protect your drivers by taking a proactive approach to driver safety. Combine real-time risk identification, automated coaching, and the Safety Score to help lower operating costs, make your department more productive, and reduce accidents by up to 30%. Ready to supercharge your safety team?`,
    learnMoreUrl: 'https://gomotive.com/products/fleet-safety/',
    isTrialBtn: true,
    isUpgradeBtn: true,
    isGetQuoteBtn: false,
    isUpgradeRequestedBtn: true,
    isGetQuoteRequestedBtn: false,
  },
  ifta: {
    upsellId: 'ifta',
    staticImgUrl: getStaticImgUrl('upsell', 'upsell-IFTA-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('ifta'),
    title: $localize`Upgrade to Pro to unlock IFTA Reporting`,
    description: $localize`Save time and reduce errors with automated IFTA reporting. With Motive, you can automatically calculate distance traveled and fuel purchased by jurisdiction, upload receipts in bulk, and export ready-to-file reports that are free from clerical errors and inconsistencies.`,
    learnMoreUrl: 'https://gomotive.com/products/ifta-fuel-tax-reporting',
    isTrialBtn: true,
    isUpgradeBtn: true,
    isGetQuoteBtn: false,
    isUpgradeRequestedBtn: true,
    isGetQuoteRequestedBtn: false,
  },
  addDashCam: {
    upsellId: 'add-dash-cam',
    staticImgUrl: getStaticImgUrl('upsell', 'dashcam-event-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('addDashCam'),
    title: $localize`Add dashcams to access Event Tagging`,
    description: $localize`Motive’s AI-powered Smart Dashcam analyzes each event to tag potential causes like tailgating, driver distraction, and more. `,
    learnMoreUrl: 'https://gomotive.com/products/dashcam',
    isTrialBtn: false,
    isUpgradeBtn: false,
    isGetQuoteBtn: true,
    isUpgradeRequestedBtn: false,
    isGetQuoteRequestedBtn: true,
  },
  addDashCamEvents: {
    upsellId: 'add-dashcam-events',
    staticImgUrl: getStaticImgUrl('upsell', 'dashcam-event-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('addDashCamEvents'),
    title: $localize`Add dashcams to access Event Tagging`,
    description: $localize`Motive’s AI-powered Smart Dashcam analyzes each event to tag potential causes like tailgating, driver distraction, and more. `,
    learnMoreUrl: 'https://gomotive.com/products/dashcam',
    isTrialBtn: false,
    isUpgradeBtn: false,
    isGetQuoteBtn: true,
    isUpgradeRequestedBtn: false,
    isGetQuoteRequestedBtn: true,
  },
  addDashCamFleetLive: {
    upsellId: 'add-dashcam-fleetlive',
    staticImgUrl: getStaticImgUrl('upsell', 'dashcam-event-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('addDashCamFleetLive'),
    title: $localize`Improve fleet safety and reduce risk with AI Dashcams`,
    description: $localize`Capture HD video evidence of the road to exonerate drivers and streamline insurance claims. Prevent accidents by creating personalized driver coaching using dashcam video.`,
    learnMoreUrl: 'https://gomotive.com/products/driver-safety/',
    isTrialBtn: false,
    isUpgradeBtn: false,
    isGetQuoteBtn: true,
    isUpgradeRequestedBtn: false,
    isGetQuoteRequestedBtn: true,
  },
  vehicleUtilization: {
    upsellId: 'vehicle-utilization',
    staticImgUrl: getStaticImgUrl('upsell', 'upsell-utilization-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('vehicleUtilization'),
    title: $localize`Upgrade to Pro to unlock Utilization Reports`,
    description: $localize`Track fuel usage and idling time to see inefficiencies and reduce costs. With these reports, you can see which drivers are idling excessively, track MPGs by driver and vehicle, and view idling and utilization rates for your entire fleet or by vehicle, driver, or terminal groups.`,
    learnMoreUrl: 'https://gomotive.com/products/sustainability',
    isTrialBtn: true,
    isUpgradeBtn: true,
    isGetQuoteBtn: false,
    isUpgradeRequestedBtn: true,
    isGetQuoteRequestedBtn: false,
  },
  agUpsell: {
    upsellId: 'ag-upsell',
    staticImgUrl: getStaticImgUrl('upsell', 'upsell-ag-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('agUpsell'),
    title: $localize`Track trailers and equipment with Asset Gateways`,
    description: $localize`Manage assets in real time, see location history, prevent theft, and monitor utilization with wired and solar-powered GPS trackers.`,
    learnMoreUrl: 'https://gomotive.com/products/tracking-telematics',
    isTrialBtn: false,
    isUpgradeBtn: false,
    isGetQuoteBtn: true,
    isUpgradeRequestedBtn: false,
    isGetQuoteRequestedBtn: true,
  },
  vgUpsell: {
    upsellId: 'vg-upsell',
    staticImgUrl: getStaticImgUrl('upsell', 'upsell-vg-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('vgUpsell'),
    title: $localize`Track and manage all your vehicles with Vehicle Gateways`,
    description: $localize`Track vehicle locations, stay compliant, monitor maintenance, track fuel usage, and more with the Vehicle Gateway.`,
    learnMoreUrl: 'https://gomotive.com/products/tracking-telematics',
    isTrialBtn: false,
    isUpgradeBtn: false,
    isGetQuoteBtn: true,
    isUpgradeRequestedBtn: false,
    isGetQuoteRequestedBtn: true,
  },
  general_upgrade: {
    upsellId: 'all-pro-features',
    imgUrl: getStaticImgUrl('upsell', 'upsell-general-upgrade-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('general_upgrade'),
    staticImgUrl: getStaticImgUrl('upsell', 'upsell-general-upgrade-video-poster.jpg'),
    title: $localize`Upgrade to Pro to unlock powerful features`,
    learnMoreUrl: 'https://gomotive.com/pro-plan',
    checkList: [
      $localize`Safety Hub provides advanced driver safety tools to improve coaching and prevent accidents.`,
      $localize`Automated IFTA Reporting calculates distance traveled and fuel purchased by jurisdiction.`,
      $localize`Vehicle Utilization Reports track fuel usage and idling time across your fleet.`,
    ],
    isTrialBtn: true,
    isUpgradeBtn: true,
    isUpgradeRequestedBtn: true,
    isGetQuoteRequestedBtn: false,
  },
  fuel: {
    upsellId: 'fuel',
    staticImgUrl: getStaticImgUrl('upsell', 'upsell-fuel-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('fuel'),
    title: $localize`Unlock Fuel Hub for free`,
    description: $localize`Cut costs and operate more efficiently with the all new Fuel Hub. In addition to automated IFTA reporting, you\'ll have actionable insights about drivers and vehicles to see where your fleet\'s losing money and how to improve fuel performance—all in one place.`,
    learnMoreUrl: 'https://gomotive.com/products/maintenance',
    isTrialBtn: true,
    isUpgradeBtn: true,
    isGetQuoteBtn: false,
    isUpgradeRequestedBtn: true,
    isGetQuoteRequestedBtn: false,
  },
  maintenance: {
    upsellId: 'maintenance',
    staticImgUrl: getStaticImgUrl('upsell', 'upsell-maintenance-video-poster.jpg'),
    // videoUrl: getLocaleSpecificVideoLink('maintenance'),
    title: $localize`Unlock a complete Maintenance Solution`,
    description: $localize`Being proactive is key to sustaining a healthy fleet of vehicles and assets, which is why our Pro plan offers automated reporting, maintenance reminders, and real-time engine fault code alerts to help increase vehicle uptime and reduce repair costs. `,
    learnMoreUrl: 'https://gomotive.com/products/maintenance?utm_source=maintenance_upsell',
    isTrialBtn: true,
    isUpgradeBtn: true,
    isGetQuoteBtn: false,
    isUpgradeRequestedBtn: true,
    isGetQuoteRequestedBtn: false,
  },
  fleet_card_upsell_admin: {
    upsellId: 'fleet-card-upsell-admin',
    staticImgUrl: getStaticImgUrl('fleet-cards', 'fleet-cards-waitlist.png'),
    title: $localize`Save big with Motive Cards`,
    description: $localize`Get competitive discounts on fuel and other business expenses in the convenience of a card.`,
    fiveCentsOffDescription: $localize`Get competitive discounts on fuel and other business expenses, like 5¢ off per gallon of diesel, all in the convenience of a card.`,
    threeCentsOffDescription: $localize`Get competitive discounts on fuel and other business expenses, like 3¢ off per gallon of diesel, all in the convenience of a card.`,
    successDescription:$localize`Thanks for your interest! We’re as pumped as you about this new offering and will reach out with details on how to apply.`,
    isApplyNowBtn: true,
    isImInterestedBtn: false,
    learnMoreFcUrl: '/fleet-cards/upsell/benefits',
  },
  fleet_card_upsell_admin_application_started: {
    upsellId: 'fleet-card-upsell-admin-application-started',
    staticImgUrl: getStaticImgUrl('fleet-cards', 'fleet-cards-waitlist.png'),
    title: $localize`Your application is almost complete`,
    description_with_updatedDate: $localize`Your Motive Card application was last saved on {{updatedDate}}, at {{updatedTime}}.`,
    description_with_updatedBy_and_updatedDate: $localize`Your Motive Card application was last saved by {{updatedBy}} on {{updatedDate}}, at {{updatedTime}}.`,
    successDescription: $localize`Thanks for your interest! We’re as pumped as you about this new offering and will reach out with details on how to apply.`,
    isCompleteMyApplicationBtn: true,
  },
  fleet_card_upsell_user: {
    upsellId: 'fleet-card-upsell-user',
    staticImgUrl: getStaticImgUrl('fleet-cards', 'fleet-cards-waitlist.png'),
    title: $localize`Save big with Motive Cards`,
    description: $localize`Get competitive discounts on fuel and other business expenses in the convenience of a card.`,
    fiveCentsOffDescription: $localize`Get competitive discounts on fuel and other business expenses, like 5¢ off per gallon of diesel, all in the convenience of a card.`,
    threeCentsOffDescription: $localize`Get competitive discounts on fuel and other business expenses, like 3¢ off per gallon of diesel, all in the convenience of a card.`,
    successDescription:$localize`Thanks for your interest! We’re as pumped as you about this new offering and will reach out with details on how to apply.`,
    isApplyNowBtn: false,
    isImInterestedBtn: true,
    learnMoreFcUrl: '/fleet-cards/upsell/benefits',
  },
  cmrl_mm_ent_expansion: { // key set in feature flag (upsell-entry-popup)
    upsellId: 'cmrl-mm-ent-expansion',
    staticImgUrl: 'https://go.gomotive.com/rs/264-HRT-423/images/AI_Dashcam_Comparison.gif',
    title: $localize`Independent study confirms Motive AI Dashcam is most accurate.`,
    description: $localize`Motive accurately detects unsafe behavior 89% of the time, outperforming its competitors. Customers also see 22% fewer accidents with Motive dash cams and frequent coaching. Upgrade now. The AI Dashcam is compatible with Vehicle Gateway 3.55 and higher.`,
    successDescription: $localize`Thanks for requesting demo, your account manager will reach out to you within 1 business day.`,
    learnMoreUrl: 'https://gomotive.com/products/dashcam/best-dash-cam/?utm_campaign=WAPP-2022-05-AI-Dashcam-Upsell-Web&utm_medium=native&utm_source=dashboard-app',
    isGetDemoBtn: true,
    isGetDemoRequestedBtn: true,
    wrapDescText: true,
  },
  smb_promo_expansion: { // key set in feature flag (upsell-entry-popup)
    upsellId: 'smb-promo-expansion',
    staticImgUrl: 'https://go.gomotive.com/rs/264-HRT-423/images/2022Q2_Upsell_SMB%20Expansion.png',
    title: $localize`3 months free on your subscription. No hardware cost. You’ve earned it.`,
    description: $localize`You’ve been with us as we’ve evolved into this new, exciting chapter as Motive, so we’d like to present this exclusive, limited time offer: up to 3 months free on your subscription and free hardware on any new Smart Dashcam or Vehicle Gateway. Call sales today at +1 844-621-2633 to claim your savings.`,
    successDescription: $localize`Request received. Your account manager will reach out to you within one business day.`,
    isActivateSaving: true,
    isActivateSavingRequestedBtn: true,
    wrapDescText: true,
  },
  followModeUpsell: {
    upsellId: 'follow-mode-upsell',
    staticImgUrl: getStaticImgUrl('upsell', 'follow-mode-upsell.png'),
    title: $localize`Get real-time tracking`,
    description: $localize`See this vehicle’s current location and turn-by-turn progress when you upgrade to the latest Vehicle Gateway.`,
    learnMoreUrl: 'https://gomotive.com/products/tracking-telematics',
    isTrialBtn: false,
    isUpgradeBtn: false,
    isGetQuoteBtn: true,
    isUpgradeRequestedBtn: false,
    isGetQuoteRequestedBtn: true,
  },
  followModeUpsellNoEld: {
    upsellId: 'follow-mode-upsell-no-eld',
    staticImgUrl: getStaticImgUrl('upsell', 'follow-mode-upsell.png'),
    title: $localize`Get real-time tracking`,
    description: $localize`See this vehicle’s current location and turn-by-turn progress when you add a Vehicle Gateway.`,
    learnMoreUrl: 'https://gomotive.com/products/tracking-telematics',
    isTrialBtn: false,
    isUpgradeBtn: false,
    isGetQuoteBtn: true,
    isUpgradeRequestedBtn: false,
    isGetQuoteRequestedBtn: true,
  },
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_ADMIN_V1]: { // key set in feature flag (upsell-entry-popup)
    upsellId: 'existing-cx-cards-upsell-admin-v1',
    staticImgUrl: getStaticImgUrl('upsell', '2022Q3_Upsell_Motive_Cards_Admin_v1@2x.png'),
    title: $localize`Save on fuel, maintenance, and more with the no-fee Motive Card.`,
    description: $localize` Get diesel savings averaging 20 cents per gallon at partner stations, including Love\'s and TA. Save 5 cents per gallon everywhere else. The Motive Card is free to use. No activation fees. No membership fees. No hidden charges.`,
    learnMoreUrl: '/fleet-cards/upsell/benefits',
    isApplyNowBtn: true,
  },
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_ADMIN_V2]: { // key set in feature flag (upsell-entry-popup)
    upsellId: 'existing-cx-cards-upsell-admin-v2',
    staticImgUrl: getStaticImgUrl('upsell', '2022Q3_Upsell_Motive_Cards_Admin_v2@2x.png'),
    title: $localize`Save on fuel, maintenance, and more with the no-fee Motive Card.`,
    description: $localize` Get diesel savings averaging 20 cents per gallon at partner stations, including Love\'s and TA. Save 5 cents per gallon everywhere else. The Motive Card is free to use. No activation fees. No membership fees. No hidden charges.`,
    learnMoreUrl: '/fleet-cards/upsell/benefits',
    isApplyNowBtn: true,
  },
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.CARDS_UPSELL_POPUP_NON_ADMIN]: { // key set in feature flag (upsell-entry-popup)
    upsellId: 'existing-cx-cards-upsell-non-admin',
    staticImgUrl: getStaticImgUrl('upsell', '2022Q3_Upsell_Motive_Cards_Non-Admin@2x.png'),
    title: $localize`Save on fuel, maintenance, and more with the no-fee Motive Card.`,
    description: $localize` Get diesel savings averaging 20 cents per gallon at partner stations, including Love\'s and TA. Save 5 cents per gallon everywhere else. The Motive Card is free to use. No activation fees. No membership fees. No hidden charges.`,
    isImInterestedBtn: true,
  },
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOY_2022_GENERAL_DASHCAM_UPSELL]: { // key set in feature flag (upsell-entry-popup)
    upsellId: 'eoy-2022-general-dashcam-eoy-2022',
    staticImgUrl: 'https://go.gomotive.com/rs/264-HRT-423/images/upsell-eoy-2022-promo.jpg',
    title: $localize`Unwrap savings on hardware.`,
    description: $localize`For a limited time only, pay $0 for hardware and receive up to 6 months free. Some restrictions apply. `,
    successDescription: $localize`Thanks for requesting demo, your account manager will reach out to you within 1 business day.`,
    isGetTheDealBtn: true,
    isGetTheDealRequestedBtn: true,
    wrapDescText: true,
  },
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOY_2022_FLEETVIEW_DASHCAM_UPSELL]: { // key set in feature flag (upsell-entry-popup)
    upsellId: 'smb-fleetview-dashcam-eoy-2022',
    staticImgUrl: 'https://go.gomotive.com/rs/264-HRT-423/images/upsell-eoy-2022-promo.jpg',
    title: $localize`Unwrap Savings on hardware.`,
    description: $localize`For a limited time only, pay $0 for hardware and receive up to 6 months free. Some restrictions apply. `,
    successDescription: $localize`Thanks for requesting demo, your account manager will reach out to you within 1 business day.`,
    isGetTheDealBtn: true,
    isGetTheDealRequestedBtn: true,
    wrapDescText: true,
  },
  [FEATURE_FLAG_UPSELL_CONFIG_KEYS.EOQ_Q1_2023_UPSELL]: { // key set in feature flag (upsell-entry-popup)
    upsellId: UPSELL_CAMPAIGN_NAMES.smb_cards_sweepstakes,
    staticImgUrl: getStaticImgUrl('upsell', '2023Q1_Upsell_Motive_Cards_sale.jpg'),
    title: $localize`Get Motive Card.\nWin $5,000 in credit.`,
    description: $localize`Apply and get approved before April 15 and enter to win up to $5,000 in credit. No hidden fees. Universally accepted. Get discounts on fuel, maintenance and more. Sweepstakes Terms apply.`,
    learnMoreUrl: 'https://go.gomotive.com/motive-cards-sweepstakes-23.html',
    isApplyNowBtn: true,
  },
};

export const UPSELL_SUCCESS: { [key: string]: ISuccess } = {
  default: {
    heading: $localize`Pro Trial Activated`,
    description: $localize`You now have access to following Pro features for 30 days.`,
    featuresList: [
      $localize`Safety Hub provides the driver safety tools to help improve coaching & prevent accidents.`,
      $localize`Automated IFTA Reporting calculates distance traveled and fuel purchased by jurisdiction.`,
      $localize`Fuel Hub provides the insights to help make smarter choices around fuel usage & save money.`,
    ],
  },
  // Once maintenance upsell is enabled this will be default content.
  maintenance: {
    heading: $localize`Pro Trial Activated`,
    description: $localize`You now have access to following Pro features for 30 days.`,
    featuresList: [
      $localize`Improve coaching and prevent accidents with advanced driver safety tools in the Safety Hub.`,
      $localize`Automate IFTA reporting, monitor vehicle idling, and maximize fuel efficiency using the Fuel Hub.`,
      $localize`Minimize vehicle downtime and reduce repair costs with automated maintenance reminders.`,
      $localize`Stay connected and save money with included in-cab WiFi hotspot.`,
    ],
  },
};

 // Specific values to be provided by marketing ops
export const DEFAULT_UPSELL_PARAMS: IGenericUpsellDetails = {
  campaign_type: '',
  url_of_most_recent_mst_fill: window.location.href,
  offer_type: '',
  cta_content: '',
  fe_test_info: '',
  product_interest: '',
  upsell_product_interest: '',
  utm_params: {
    utm_term: '',
    utm_medium: 'native',
    utm_source: 'dashboard-app',
    utm_content: '',
    utm_campaign: '', // this is mandatory
  },
};

// TODO: Update the properties like  cta_content, product_interest, upsell_product_interest
//       according to each upsell
export const UPSELL_CAMPAIGNS_MAP: { [key: string]: IGenericUpsellDetails } = {
  [UPSELL_CAMPAIGN_NAMES.get_quote_vg]: { // get_quote_vg in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Get Quote',
    upsell_product_interest: 'ELD Compliance',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.get_quote_vg),
  },
  [UPSELL_CAMPAIGN_NAMES.upgrade_now]: { // upgrade_now in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Upgrade Now',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.upgrade_now),
  },
  [UPSELL_CAMPAIGN_NAMES.get_quote]: { // get_quote in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Get Quote',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.get_quote),
  },
  [UPSELL_CAMPAIGN_NAMES.get_quote_ag]: { // get_quote_ag in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Get Quote',
    product_interest: 'Trailer Tracking',
    upsell_product_interest: '',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.get_quote_ag),
  },
  [UPSELL_CAMPAIGN_NAMES.get_quote_dashcam]: { // get_quote_dashcam in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Get Quote',
    product_interest: 'Smart Dashcam',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.get_quote_dashcam),
  },
  [UPSELL_CAMPAIGN_NAMES.get_quote_add_more_units]: { // get_quote_add_more_units in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Get Quote',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.get_quote_add_more_units),
  },
  [UPSELL_CAMPAIGN_NAMES.cpq_add]: { // cpq_add in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Get Quote',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.cpq_add),
  },
  [UPSELL_CAMPAIGN_NAMES.cpq_upgrade]: { // cpq_upgrade in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Get Quote',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.cpq_upgrade),
  },
  [UPSELL_CAMPAIGN_NAMES.fleet_cards]: { // fleet_cards in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Motive Card',
    cta_content: 'Apply now',
    product_interest: 'Fleet Cards',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.fleet_cards),
  },
  [UPSELL_CAMPAIGN_NAMES.fleet_cards_estimated_savings_apply_now]: { // fleet_cards in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Apply Now',
    cta_content: 'Apply Now',
    product_interest: 'Fleet Cards',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.fleet_cards_estimated_savings_apply_now),
  },
  [UPSELL_CAMPAIGN_NAMES.maintenance_pro]: { // maintenance_pro in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Upgrade Now',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.maintenance_pro),
  },
  [UPSELL_CAMPAIGN_NAMES.get_demo_ai_dashcam]: { // get_demo_ai_dashcam in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Request Demo',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.get_demo_ai_dashcam),
  },
  [UPSELL_CAMPAIGN_NAMES.smb_promo]: { // smb_promo in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Request Demo',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.smb_promo),
  },
  [UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up]: { // cards_upsell_pop_up in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Motive Card',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up),
  },
  [UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up_apply_now]: { // cards_upsell_pop_up in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Motive Card',
    cta_content: 'Apply now',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up),
  },
  [UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up_interested]: { // cards_upsell_pop_up in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Motive Card',
    cta_content: 'I\'m interested',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.cards_upsell_pop_up),
  },
  [UPSELL_CAMPAIGN_NAMES.general_dashcam_upsell]: { // general_dashcam_upsell in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Request Demo',
    cta_content: 'Get the deal',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.general_dashcam_upsell),
  },
  [UPSELL_CAMPAIGN_NAMES.fleetview_dashcam_upsell]: { // fleetview_dashcam_upsell in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Request Demo',
    cta_content: 'Get the deal',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.fleetview_dashcam_upsell),
  },
  [UPSELL_CAMPAIGN_NAMES.smb_cards_sweepstakes]: { // smb_cards_sweepstakes in k2web
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Motive Card',
    utm_params: {
      ...DEFAULT_UPSELL_PARAMS.utm_params,
      utm_medium: 'pop-up',
      utm_campaign: UPSELL_CAMPAIGN_NAMES.smb_cards_sweepstakes,
    },
  },
  [UPSELL_CAMPAIGN_NAMES.shop_evergreen_upsell]: {
    ...DEFAULT_UPSELL_PARAMS,
    offer_type: 'Contact Sales',
    cta_content: 'I\'m interested',
    utm_params: updateUTMCampaign(UPSELL_CAMPAIGN_NAMES.shop_evergreen_upsell),
  },
};
